import React, { useState } from "react";
import axios from "axios";
import { X, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { getStoredReferral } from "@/lib/referrals";
import { TRAVEL_ZONES, TRAVEL_TBC_MESSAGE, PRICE_FOOTNOTE, travelQuote, zoneLabel } from "@/lib/travel";
import BookingDateSlotPicker from "@/components/BookingDateSlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "60183628663";

const T = {
    en: {
        lock: "Lock your date · 50% deposit", scopeTitle: "Your requirements", scopeIn: "My requirements are within the listed package scope",
        scopeOut: "I need something extra (models, more products / setups, styling, voice-over, extra versions, urgent delivery…)",
        scopeMsg: "Additional production requirements will require a separate quotation.", scopeCta: "Request a custom quotation on WhatsApp",
        location: "Filming Location", locationPh: "Select the filming location", pkgLine: "Package", travelLine: "Travel charge",
        included: "Included", tbc: "To be confirmed", total: "Total", deposit: "Deposit today (50%)", balance: "Balance on delivery",
        email: "Email", emailHelp: "We'll send the receipt + shoot updates here", name: "Name", namePh: "Your name / cafe",
        phoneHelp: "Optional · Kaily WhatsApps within 24h to lock the date", pay: (d) => `Pay RM ${d} · Lock your date`, redirect: "Redirecting to Stripe…",
        secure: "Secured by Stripe · Visa / Mastercard / FPX · no card data stored", badEmail: "Please enter a valid email",
        needLoc: "Please select the filming location", needScope: "Please confirm your requirements are within scope, or request a custom quotation",
        failed: "Could not start checkout. Please try again.", unavailable: "Payment service unavailable",
    },
    cn: {
        lock: "锁定档期 · 50% 定金", scopeTitle: "你的需求", scopeIn: "我的需求在所列套餐范围之内",
        scopeOut: "我需要额外项目(模特、更多产品 / 场景、造型、配音、额外版本、加急交付……)",
        scopeMsg: "额外的制作需求将需要另行报价。", scopeCta: "在 WhatsApp 索取定制报价",
        location: "拍摄地点", locationPh: "请选择拍摄地点", pkgLine: "套餐", travelLine: "旅程费用",
        included: "已包含", tbc: "待确认", total: "总价", deposit: "今日定金(50%)", balance: "交付后尾款",
        email: "邮箱", emailHelp: "用于发送付款收据和拍摄通知", name: "姓名", namePh: "你的名字 / 店名",
        phoneHelp: "可选 · Kaily 24h 内会联系你定档期", pay: (d) => `付 RM ${d} 锁定档期`, redirect: "正在跳转 Stripe…",
        secure: "由 Stripe 安全处理 · 支持 Visa / Mastercard / FPX · 不储存卡片信息", badEmail: "请输入有效的邮箱地址",
        needLoc: "请选择拍摄地点", needScope: "请确认你的需求在范围内,或索取定制报价",
        failed: "无法创建支付会话,请重试。", unavailable: "支付服务暂时无法连接",
    },
};

function quoteHref(pkg, lang) {
    const name = lang === "cn" ? (pkg.name_cn || pkg.name_en) : pkg.name_en;
    const text = lang === "cn"
        ? `Hi Kaily!我对 ${name} 有兴趣,但需要一些超出套餐范围的额外项目。可以帮我做一份定制报价吗?`
        : `Hi Kaily! I'm interested in ${name} but need a few requirements outside the listed scope. Could you prepare a custom quotation?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

export default function BookingModal({ open, onClose, pkg, lang = "en", zones = TRAVEL_ZONES }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [scope, setScope] = useState("");       // "in" | "out"
    const [location, setLocation] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [hold, setHold] = useState(null);     // { hold_id, expires_at }
    const [slot, setSlot] = useState(null);     // { shoot_date, time_slot }

    if (!pkg) return null;
    const isCn = lang === "cn";
    const s = T[isCn ? "cn" : "en"];

    const travel = location ? travelQuote(location, pkg, zones) : null;
    const pkgRm = pkg.price_rm;
    const travelRm = travel?.status === "charged" ? travel.travel_rm : 0;
    const totalRm = pkgRm + travelRm;
    const depositRm = Math.floor(totalRm / 2);
    const balanceRm = totalRm - depositRm;
    const inputCls = "mt-1.5 bg-white border-cafe-espresso/10 rounded-xl h-11";
    const labelCls = "text-xs uppercase tracking-[0.15em] text-cafe-mocha font-semibold";

    const submit = async (e) => {
        e?.preventDefault?.();
        if (scope !== "in") return toast.error(s.needScope);
        if (!location) return toast.error(s.needLoc);
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error(s.badEmail);
        if (!hold || !slot) return toast.error(isCn ? "请选择档期和时段" : "Please pick a date & time slot");
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/shoot-checkout`, {
                package_id: pkg.id,
                customer_email: email.trim().toLowerCase(),
                customer_name: name.trim() || null,
                customer_phone: phone.trim() || null,
                referral_code: getStoredReferral() || null,
                filming_location: location,
                scope_confirmed: true,
                lang,
                origin_url: window.location.origin,
                hold_id: hold.hold_id,
                shoot_date: slot.shoot_date,
                time_slot: slot.time_slot,
            }, { timeout: 15000 });
            if (data?.checkout_url) {
                try { window.sessionStorage.setItem("slowcafe_pending_order", data.order_id); } catch { /* ignore */ }
                window.location.href = data.checkout_url;
                return;
            }
            toast.error(s.failed);
        } catch (err) {
            toast.error(err?.response?.data?.detail || s.unavailable);
        } finally {
            setSubmitting(false);
        }
    };

    const releaseHold = async () => {
        if (!hold?.hold_id) return;
        try { await axios.delete(`${API}/booking/hold-slot/${hold.hold_id}`); } catch { /* non-blocking */ }
        setHold(null); setSlot(null);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { releaseHold(); onClose(); } }}>
            <DialogContent data-testid="booking-modal" className="bg-cafe-cream border border-cafe-espresso/10 rounded-3xl p-0 max-w-md max-h-[92vh] overflow-y-auto [&>button]:hidden">
                <VisuallyHidden asChild><DialogTitle>{isCn ? `预订 ${pkg.name_cn || pkg.name_en}` : `Book ${pkg.name_en}`}</DialogTitle></VisuallyHidden>
                <VisuallyHidden asChild><DialogDescription>{s.lock}</DialogDescription></VisuallyHidden>
                <div className="p-7 md:p-9">
                    <button onClick={() => { releaseHold(); onClose(); }} aria-label="Close" data-testid="booking-modal-close"
                        className="absolute top-5 right-5 h-9 w-9 rounded-full flex items-center justify-center text-cafe-espresso/60 hover:text-cafe-espresso hover:bg-cafe-espresso/5 transition-colors">
                        <X className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-cafe-olive font-semibold mb-3">{s.lock}</div>
                    <h2 className="font-display text-3xl tracking-[-0.025em] text-cafe-espresso mb-5 leading-tight">{isCn ? (pkg.name_cn || pkg.name_en) : pkg.name_en}</h2>

                    <form onSubmit={submit} className="space-y-5">
                        {/* Scope confirmation */}
                        <fieldset data-testid="booking-modal-scope" className="space-y-2">
                            <legend className={labelCls}>{s.scopeTitle} *</legend>
                            {[["in", s.scopeIn], ["out", s.scopeOut]].map(([v, label]) => (
                                <label key={v} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${scope === v ? "border-cafe-olive bg-cafe-olive/5" : "border-cafe-espresso/10 bg-white hover:border-cafe-olive/40"}`}>
                                    <input type="radio" name="scope" value={v} checked={scope === v} onChange={() => setScope(v)} data-testid={`booking-modal-scope-${v}`} className="mt-1 accent-[#6B7A3C]" />
                                    <span className="text-[13px] text-cafe-espresso leading-snug">{label}</span>
                                </label>
                            ))}
                            {scope === "out" && (
                                <div className="rounded-xl border border-[#B8865B]/40 bg-[#D4A574]/10 p-4" data-testid="booking-modal-scope-out-msg">
                                    <div className="flex items-start gap-2 text-sm text-cafe-espresso font-medium leading-snug"><AlertCircle className="h-4 w-4 mt-0.5 text-[#B8865B] flex-shrink-0" />{s.scopeMsg}</div>
                                    <a href={quoteHref(pkg, lang)} target="_blank" rel="noopener noreferrer" data-testid="booking-modal-quote-link"
                                        className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-cafe-olive underline underline-offset-4">{s.scopeCta} <ArrowRight className="h-3.5 w-3.5" /></a>
                                </div>
                            )}
                        </fieldset>

                        {scope === "in" && (
                            <>
                                <div>
                                    <Label className={labelCls}>{s.location} *</Label>
                                    <select value={location} onChange={(e) => setLocation(e.target.value)} required data-testid="booking-modal-location"
                                        className="mt-1.5 w-full bg-white border border-cafe-espresso/10 rounded-xl h-11 px-3 text-sm text-cafe-espresso">
                                        <option value="">{s.locationPh}</option>
                                        {zones.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z, lang)}</option>)}
                                    </select>
                                </div>

                                <div className="bg-white border border-cafe-espresso/10 rounded-2xl p-4 text-sm space-y-1.5" data-testid="booking-modal-summary">
                                    <div className="flex justify-between"><span className="text-cafe-mocha">{s.pkgLine}</span><span className="text-cafe-espresso tabular-nums">RM {pkgRm}</span></div>
                                    <div className="flex justify-between">
                                        <span className="text-cafe-mocha">{s.travelLine}</span>
                                        <span className="text-cafe-espresso tabular-nums" data-testid="booking-modal-travel">
                                            {!travel ? "—" : travel.status === "charged" ? `RM ${travel.travel_rm}` : travel.status === "tbc" ? s.tbc : s.included}
                                        </span>
                                    </div>
                                    {travel?.status === "tbc" && <p className="text-xs text-[#8A6A3E] leading-snug pt-1" data-testid="booking-modal-travel-tbc">{TRAVEL_TBC_MESSAGE[isCn ? "cn" : "en"]}</p>}
                                    <div className="flex justify-between pt-2 border-t border-cafe-espresso/10 font-semibold"><span className="text-cafe-espresso">{s.total}</span><span className="text-cafe-espresso tabular-nums" data-testid="booking-modal-total">RM {totalRm}</span></div>
                                    <div className="flex justify-between"><span className="text-cafe-olive">{s.deposit}</span><span className="text-cafe-olive font-semibold tabular-nums" data-testid="booking-modal-deposit">RM {depositRm}</span></div>
                                    <div className="flex justify-between"><span className="text-cafe-mocha">{s.balance}</span><span className="text-cafe-mocha tabular-nums">RM {balanceRm}</span></div>
                                    <p className="text-[10.5px] text-cafe-mocha/70 leading-snug pt-2">{PRICE_FOOTNOTE[isCn ? "cn" : "en"]}</p>
                                </div>

                                <div>
                                    <Label className={labelCls}>{s.email} *</Label>
                                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@cafe.com" data-testid="booking-modal-email" required className={inputCls} />
                                    <p className="text-[11px] text-cafe-mocha/70 mt-1.5">{s.emailHelp}</p>
                                </div>
                                <div>
                                    <Label className={labelCls}>{s.name}</Label>
                                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={s.namePh} data-testid="booking-modal-name" className={inputCls} />
                                </div>
                                <div>
                                    <Label className={labelCls}>WhatsApp</Label>
                                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+60 12 345 6789" data-testid="booking-modal-phone" className={inputCls} />
                                    <p className="text-[11px] text-cafe-mocha/70 mt-1.5">{s.phoneHelp}</p>
                                </div>

                                {email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
                                    <div className="pt-1">
                                        <Label className={labelCls}>{isCn ? "拍摄日期和时段 *" : "Shoot date & time slot *"}</Label>
                                        <div className="mt-2">
                                            <BookingDateSlotPicker
                                                packageId={pkg.id}
                                                filmingLocation={location}
                                                customerEmail={email.trim().toLowerCase()}
                                                customerName={name}
                                                isCn={isCn}
                                                onHoldReady={(h, sl) => { setHold(h); setSlot(sl); }}
                                                onReleaseHold={() => { setHold(null); setSlot(null); }}
                                            />
                                        </div>
                                    </div>
                                )}

                                <button type="submit" disabled={submitting || !location || !hold || !slot} data-testid="booking-modal-submit"
                                    className="w-full mt-2 group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-medium tracking-wide bg-gradient-to-br from-[#E8B97A] to-[#B88A55] text-[#1A0F0A] hover:from-[#F0C384] hover:to-[#C39562] shadow-[0_10px_30px_-10px_rgba(212,165,116,0.6)] disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />{s.redirect}</>) : (<>{s.pay(depositRm)}<ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} /></>)}
                                </button>
                                <p className="text-[10px] text-cafe-mocha/60 text-center leading-relaxed">{s.secure}</p>
                            </>
                        )}
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
